import { apiClient } from "./api";
import { queryOptions } from "@tanstack/react-query";

export type ProductLifecycle =
  | "internal"
  | "private_preview"
  | "public_preview"
  | "ga";

export interface AutomationConfig {
  showFrequency: boolean;
  showActions: boolean;
  showVolume: boolean;
  actionLabel: string;
  volumeLabel: string;
}

export interface Product {
  product_sku: string;
  product_family: string;
  name: string;
  product_id?: string; // New field
  credit_cost: number;
  description: string;
  active: boolean;
  product_lifecycle: ProductLifecycle;
  metadata?: Record<string, any>;
  automation_product?: boolean;
  estimated_cost?: number;
  product_owner?: string;
  automation_config?: AutomationConfig; // New field
}

export interface ProductCreate {
  product_family: string;
  name: string;
  product_id?: string; // New field
  credit_cost: number;
  description: string;
  product_lifecycle: string;
  metadata?: Record<string, any>;
  active?: boolean;
  automation_product?: boolean;
  estimated_cost?: number;
  product_owner?: string;
  automation_config?: AutomationConfig; // New field
}

export interface ProductUpdate {
  name?: string;
  product_id?: string; // New field
  credit_cost?: number;
  description?: string;
  metadata?: Record<string, any>;
  active?: boolean;
  product_lifecycle?: string;
  product_family?: string;
  automation_product?: boolean;
  estimated_cost?: number;
  product_owner?: string;
  automation_config?: Partial<AutomationConfig>; // New field, using Partial to allow updates of individual fields
}

// Get all products
export const getAllProducts = async (): Promise<Product[]> => {
  try {
    const response = await apiClient.get("/api/products");
    return response.data.products;
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error;
  }
};

// Create a new product
export const createProduct = async (data: ProductCreate): Promise<Product> => {
  try {
    const response = await apiClient.post("/api/products/create-product", data);
    return response.data;
  } catch (error) {
    console.error("Error creating product:", error);
    throw error;
  }
};

// Update an existing product
export const updateProduct = async (
  product_sku: string,
  data: ProductUpdate,
): Promise<Product> => {
  try {
    //console.log("Updating product with data:", data); // Debug log
    const response = await apiClient.put(`/api/products/${product_sku}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating product:", error);
    throw error;
  }
};

// Get a specific product
export const getProduct = async (product_sku: string): Promise<Product> => {
  try {
    const response = await apiClient.get(`/api/products/${product_sku}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching product:", error);
    throw error;
  }
};

// Get product families
export const getProductFamilies = async (): Promise<string[]> => {
  try {
    //console.log("Fetching product families...");
    const response = await apiClient.get("/api/products/families/list");
    //console.log("Product families response:", response);
    return response.data.families;
  } catch (error) {
    console.error("Error fetching product families:", error);
    throw error;
  }
};

// React Query Options
export const productQueries = {
  products: () =>
    queryOptions({
      queryKey: ["products"],
      queryFn: () => getAllProducts(),
    }),
  productFamilies: () =>
    queryOptions({
      queryKey: ["product-families"],
      queryFn: () => getProductFamilies(),
    }),
  product: (product_sku: string) =>
    queryOptions({
      queryKey: ["product", product_sku],
      queryFn: () => getProduct(product_sku),
    }),
};
